/**
 * MessageDefinition — the shape of a single translatable string declaration.
 *
 * Place these in a co-located `*.messages.ts` file next to your component:
 *
 *   import { defineMessages } from '../../i18n/messages';
 *
 *   export const HeroMessages = defineMessages({
 *     TITLE: {
 *       id: 'homepage.hero.title',
 *       text: 'Instant, Reliable Translations',
 *       description: 'Main hero headline shown above the fold',
 *     },
 *   });
 *
 * Then in your component:
 *
 *   import { HeroMessages } from './Hero.messages';
 *   const { t } = useTranslation();
 *   <h1>{t(HeroMessages.TITLE)}</h1>
 *
 * Run `npm run i18n:extract` to regenerate en-US.json from all *.messages.ts files.
 */
export interface MessageDefinition {
  /** Dot-separated translation key, e.g. 'homepage.hero.title' */
  id: string;
  /** The English source text. This becomes the value in en-US.json. */
  text: string;
  /**
   * Human-readable context for translators — describes where the text appears,
   * what surrounds it, and what any interpolation variables represent.
   * This is written to the descriptions sidecar JSON and consumed by the
   * AI translator during sync. It is NOT loaded at runtime.
   */
  description: string;
}

/**
 * defineMessages — wraps a Record of MessageDefinitions and, at runtime,
 * collapses each entry down to its `id` string.
 *
 * This means `HeroMessages.TITLE` === `'homepage.hero.title'` at runtime,
 * so you can pass it directly to `t()` without any type gymnastics.
 */
export function defineMessages<K extends string>(
  defs: Record<K, MessageDefinition>
): Record<K, string> {
  const result = {} as Record<K, string>;
  for (const key in defs) {
    result[key] = defs[key].id;
  }
  return result;
}
